import { Component, ViewChild, ViewEncapsulation, OnInit } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { Endpoint, HelpIndex, UpdateStatusType } from '../../../models/enums';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { TargetPickerTreeComponent, TargetPickerTreeType } from '../../target-picker-tree/target-picker-tree.component';
import { GenericPickerModalComponent } from '../generic-picker-modal/generic-picker-modal.component';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { DynamicFolder } from '../../../models/dynamic-folder';
import { WorkflowStep } from '../../../models/workflow';

@Component({
    standalone: false,
    selector: 'res-target-picker-modal',
    templateUrl: './target-picker-modal.component.html',
    styleUrls: ['./target-picker-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class TargetPickerModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(TargetPickerTreeComponent, { static: true }) targetPickerTreeComponent: TargetPickerTreeComponent;

    modalTitle: string;
    primaryButtonLabel: string;
    secondaryButtonLabel = 'Cancel';
    modalHelpIndex: HelpIndex;
    modalAutomationId: string;
    modalClass = 'target-picker-modal';
    isBusy = false;
    isBusyMessage = '';
    targetPickerTreeType: TargetPickerTreeType;
    updateStatusType: UpdateStatusType;
    dataSetFilterVisible: boolean;
    dataSetFilterQuery = '';
    dataSetFilterItems: Array<any> = [];
    threshold: number;
    htmlSizes: boolean;
    maximizable: boolean;
    hideThreshold: boolean;

    preselectNodePredicate: (value: GenericTreeNode, index: number, obj: GenericTreeNode[]) => boolean;


    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService) {
        super(modalService, modalRef);
        this.loadEntityData();
    }

    ngOnInit(): void {
        if (this.targetPickerTreeComponent && this.modalData && this.modalData.initialSelectedNodeData) {
            this.targetPickerTreeComponent.initialSelectedData = this.modalData.initialSelectedNodeData;
        }

        this.hideThreshold = this.modalData?.targetPickerTreeType !== TargetPickerTreeType.findNearDuplicates && this.modalData?.targetPickerTreeType !== TargetPickerTreeType.findMoreLikeThese;
    }

    loadEntityData(): void {
        if (this.modalData) {
            this.dataSetFilterQuery = this.modalData.dataSetFilter && this.modalData.dataSetFilter.length > 0 ? this.modalData.dataSetFilter.map(function (elem) { return elem.name; }).join(';') : '';
            this.dataSetFilterItems = this.modalData.dataSetFilter && this.modalData.dataSetFilter.length > 0 ? this.modalData.dataSetFilter : [];
            this.targetPickerTreeType = <TargetPickerTreeType>this.modalData.targetPickerTreeType;
            this.updateStatusType = this.modalData.updateStatusType;
            let dupType = 'Near';
            let threshold = 80;
            if (this.targetPickerTreeType === TargetPickerTreeType.findExactDuplicates) {
                dupType = 'Exact';
                threshold = 0;
            } else if (this.targetPickerTreeType === TargetPickerTreeType.findContentDuplicates) {
                dupType = 'Content';
                threshold = 0;
            }
            switch (this.targetPickerTreeType) {
                case TargetPickerTreeType.selectSearchTarget:
                    this.dataSetFilterVisible = true;
                    this.modalAutomationId = 'target-picker';
                    this.modalTitle = 'Select Target';
                    this.primaryButtonLabel = 'OK';
                    this.threshold = 0;
                    this.maximizable = true;
                    this.modalHelpIndex = HelpIndex.select_search_targets_dlg;
                    if (this.modalData.initialSelectedNodeData) {
                        const selectedHandle = this.modalData.initialSelectedNodeData.handle;
                        this.preselectNodePredicate = (value: GenericTreeNode) => {
                            if (value.data) {
                                if (value.data.view) {
                                    return value.data.view.handle === selectedHandle;
                                } else if (value.data.corpusViewHandle) {
                                    return value.data.corpusViewHandle === selectedHandle;
                                }
                                return value.data.handle === selectedHandle;
                            }
                        };
                    }
                    break;
                case TargetPickerTreeType.findNearDuplicates:
                case TargetPickerTreeType.findContentDuplicates:
                case TargetPickerTreeType.findExactDuplicates:
                    this.modalAutomationId = 'target-picker';
                    this.primaryButtonLabel = 'Search';
                    this.modalTitle = `Find ${dupType} Duplicates`;
                    this.threshold = threshold;
                    this.maximizable = false;
                    this.modalHelpIndex = HelpIndex.myedisc_find_dupe_doc_dlg;
                    break;
                case TargetPickerTreeType.findMoreLikeThis:
                    this.modalHelpIndex = HelpIndex.myedisc_morelikethis_dlg;
                    break;
                case TargetPickerTreeType.findMoreLikeThese:
                    this.modalAutomationId = 'target-picker';
                    this.primaryButtonLabel = 'Search';
                    this.modalTitle = 'Find More Like These';
                    this.threshold = 20;
                    this.maximizable = false;
                    this.modalHelpIndex = HelpIndex.myedisc_morelikethese_dlg;
                    break;
                case TargetPickerTreeType.createExportComparisonReport:
                    this.modalHelpIndex = HelpIndex.generate_export_comparison_report_dlg;
                    break;
                case TargetPickerTreeType.searchByProfile:
                    this.modalAutomationId = 'target-picker';
                    this.primaryButtonLabel = 'Search';
                    this.modalTitle = 'Search by Synthetic Document: Select Search Target';
                    this.threshold = 20;
                    this.maximizable = false;
                    this.modalHelpIndex = HelpIndex.myedisc_run_search_by_profile_dlg;
                    break;
                case TargetPickerTreeType.selectWorkflowTarget:
                    this.dataSetFilterVisible = false;
                    this.modalAutomationId = 'target-picker';
                    this.primaryButtonLabel = 'OK';
                    this.modalTitle = 'Select Target';
                    this.threshold = 0;
                    this.maximizable = true;
                    this.modalHelpIndex = HelpIndex.select_search_targets_dlg;
                    if (this.modalData.initialSelectedNodeData) {
                        const selectedHandle = this.modalData.initialSelectedNodeData.handle;
                        this.preselectNodePredicate = (value: GenericTreeNode) => {
                            if (value.data) {
                                if (value.data.view && !(value.data instanceof DynamicFolder)) {
                                    return value.data.view.handle === selectedHandle;
                                } else if (value.data.corpusViewHandle) {
                                    return value.data.corpusViewHandle === selectedHandle;
                                }
                                return value.data.handle === selectedHandle;
                            }
                        };
                    }
                    break;
                case TargetPickerTreeType.workflowReport:
                    this.dataSetFilterVisible = false;
                    this.modalAutomationId = 'target-picker';
                    this.primaryButtonLabel = 'OK';
                    this.modalTitle = 'Create Export Comparison Report: Select Export';
                    this.maximizable = false;
                    this.threshold = 0;
                    this.htmlSizes = false; // False means unchecked; undefined means unused & hidden
                    this.modalHelpIndex = HelpIndex.generate_export_comparison_report_dlg;
                    break;
                default: console.log('You need to set parameters for the target picker in this mode in target-picker-modal.component.ts');
            }
        }
    }

    isPrimaryButtonEnabled(): boolean {
        return Boolean(this.targetPickerTreeComponent && this.targetPickerTreeComponent.selectedNode);
    }

    selectDataSets(): void {
        this.commonService.openModal(GenericPickerModalComponent, { modalData: { entityType: 'datasets', items: this.dataSetFilterItems } }).then(response => {
            if (response && response.modalResult && response.modalData) {
                this.dataSetFilterItems = response.modalData;
                this.dataSetFilterQuery = response.modalData.map(function (elem) { return elem.name; }).join(';');
            }
        });
    }

    clearDataSetFilter(): void {
        this.dataSetFilterQuery = '';
        this.dataSetFilterItems = [];
    }

    doubleClickAction(node: GenericTreeNode): void {
        this.ok();
    }

    ok(): void {
        if (this.targetPickerTreeComponent.selectedNode?.data instanceof WorkflowStep) {
            const searchTaskHandle = this.targetPickerTreeComponent.selectedNode.data?.responseHandle;
            if (searchTaskHandle) {
                this.restService.post(Endpoint.taskManager_getTasks, { taskHandles: [searchTaskHandle] },).then((response) => {
                    if (response?.tasks && response.tasks.length === 1) {
                        this.targetPickerTreeComponent.selectedNode.data.description = response.tasks[0].description;
                    }
                    this.executeModalClose();
                });
            } else {
                this.executeModalClose();
            }
        } else {
            this.executeModalClose();
        }
    }

    executeModalClose(): void {
        this.closeModal(true, {
            target: this.targetPickerTreeComponent.selectedNode,
            dataSetFilter: this.dataSetFilterItems,
            threshold: this.threshold,
            htmlSizes: this.htmlSizes
        });
    }
}
